import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, Button, Grid2, IconButton, InputAdornment, MenuItem, Paper, TextField, Toolbar, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getAccounts } from "../../api/account";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../hooks/useFormStyles";
import { CreateUserForm, getCreateUserFormSchema } from "../../types/forms/admin/user";
import { Role } from "../../types/interfaces/account";

interface AddUserFormProps {
  onSubmit: (values: CreateUserForm, selectedAccountId?: number) => Promise<void>;
  accountId?: number;
  availableRoles: Role[];
}

export default function AddUserForm({
  onSubmit,
  accountId,
  availableRoles
}: Readonly<AddUserFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<{ id: number; name: string }[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | undefined>(accountId);
  const formStyles = useFormStyles(true);

  const [showPassword, setShowPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (availableRoles.length > 0 && !methods.getValues("roleId")) {
      methods.setValue("roleId", availableRoles[0].id);
    }
  }, [availableRoles]);

  const validationSchema = getCreateUserFormSchema(t);

  const methods = useForm<CreateUserForm>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      roleId: undefined,
      phoneNumber: "",
    },
  });

  useEffect(() => {
    setSelectedAccountId(accountId);
  }, [accountId]);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const data = await getAccounts();

        if (accountId) {
          const found = data.find((e: any) => e.id === accountId);
          if (found) {
            setAccounts([{ id: found.id, name: found.name }]);
          } else {
            setAccounts([]);
          }
        } else {
          setAccounts(
            data.map((e: any) => ({
              id: e.id,
              name: e.name,
            }))
          );
        }
      } catch (error) {
        handleError(error);
      }
    };

    fetchAccounts();
  }, [accountId]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar>
          <Typography sx={{ flex: "1 1 100%" }} variant="h4">
            {t("admin.users.table.add-user")}
          </Typography>
        </Toolbar>

        <FormProvider {...methods}>
          <Box
            sx={{ p: 2 }}
            component="form"
            onSubmit={methods.handleSubmit((values) => {
              if (!selectedAccountId) {
                enqueueSnackbar(
                  t("admin.users.errors.account-required"),
                  { variant: "error" }
                );
                return;
              }

              if (confirmPassword !== methods.getValues("password")) {
                enqueueSnackbar(
                  t("admin.users.errors.password-mismatch"),
                  { variant: "error" }
                );
                return;
              }

              onSubmit(values, selectedAccountId);
            })}
          >
            <Grid2 container spacing={2}>
              {/* Account */}
              <Grid2 size={4}>
                <TextField
                  select
                  fullWidth
                  label={`${t("admin.accounts.account.label")} *`}
                  value={selectedAccountId ?? ""}
                  onChange={(e) => {
                    const val = e.target.value ? Number.parseInt(e.target.value, 10) : undefined;
                    setSelectedAccountId(val);
                  }}
                  disabled={!!accountId}
                  sx={{ ...formStyles }}
                >
                  {accountId ? (
                    <MenuItem value={accountId}>
                      {accounts.find((e) => e.id === accountId)?.name ?? `Conta #${accountId}`}
                    </MenuItem>
                  ) : (
                    accounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        {account.name}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid2>
              <Grid2 size={8}>
                <TextField
                  fullWidth
                  label={`${t("admin.users.name")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...methods.register("name")}
                  error={!!methods.formState.errors.name}
                  helperText={methods.formState.errors.name?.message}
                />
              </Grid2>
              <Grid2 size={12}>
                <TextField
                  fullWidth
                  label={`${t("admin.users.email")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...methods.register("email")}
                  error={!!methods.formState.errors.email}
                  helperText={methods.formState.errors.email?.message}
                />
              </Grid2>
              
              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  label={`${t("admin.users.password")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...methods.register("password")}
                  error={!!methods.formState.errors.password}
                  helperText={methods.formState.errors.password?.message}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(prev => !prev)}
                            edge="end"
                            aria-label={showPassword ? t("admin.users.hide-password") : t("admin.users.show-password")}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <TextField
                  fullWidth
                  type={showConfirm ? "text" : "password"}
                  label={`${t("admin.users.password-confirm")} *`}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirm(prev => !prev)}
                            edge="end"
                            aria-label={showConfirm ? t("admin.users.hide-password") : t("admin.users.show-password")}
                          >
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Grid2>

              <Grid2 size={6}>
                <TextField
                  fullWidth
                  label={t("admin.users.phone-number")}
                  variant="outlined"
                  sx={{ ...formStyles }}
                  {...methods.register("phoneNumber")}
                  error={!!methods.formState.errors.phoneNumber}
                  helperText={
                    methods.formState.errors.phoneNumber?.message as any
                  }
                />
              </Grid2>
              <Grid2 size={6}>
                <Controller
                  name="roleId"
                  control={methods.control}
                  render={({ field }) => (
                    <TextField
                      select
                      fullWidth
                      label={`${t("admin.users.role")} *`}
                      variant="outlined"
                      sx={{ ...formStyles }}
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        field.onChange(value === "" ? undefined : Number(value));
                      }}
                      disabled={availableRoles.length === 0}
                      error={!!methods.formState.errors.roleId}
                      helperText={
                        methods.formState.errors.roleId?.message as any
                      }
                    >
                      {availableRoles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {t(role.i18nLabel)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid2>
            </Grid2>

            {/* Action Bar */}
            <Grid2
              size={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
              }}
            >
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button type="submit" variant="contained">
                  {t("generic.create")}
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate(-1)}
                >
                  {t("generic.cancel")}
                </Button>
              </Box>
            </Grid2>
          </Box>
        </FormProvider>
      </Paper>
    </Box>
  );
}